import { useState, type FormEvent, type ReactElement } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';
import { ZendaWordmark } from '../components/ZendaWordmark';
import { Icon } from '../components/Icon';
import heroLogin from '../assets/login-hero.webp';
import '../styles/login.css';

/**
 * A tela de entrar: formulário à esquerda, o produto à direita.
 *
 * ── Por que o painel ao lado, e não um cartão centralizado ──
 * Esta URL é a primeira coisa que o analista da Meta abre, e um cartão sozinho no meio
 * da tela não diz o que o produto faz. A arte mostra o trabalho acontecendo: a
 * atendente, a conversa da paciente e a agenda do médico na mesma tela.
 *
 * ── Por que uma arte, e não os cartões montados em CSS ──
 * Eram cartões de mentira imitando o produto, e imitação em CSS envelhece contra o
 * original: bastava a linha de conversa real mudar para o login mostrar uma versão que
 * não existe mais. A arte é `webp` de 78 KB, e entra como FUNDO do painel: ela é
 * decorativa, e fundo não é anunciado por leitor de tela nem pede `alt`. Abaixo de
 * 900 px o painel desaparece: no celular a única coisa que importa é o campo de senha.
 *
 * ⚠️ O texto dentro da arte é pt-BR e não acompanha a troca de idioma. Trocar de
 * idioma pede outra arte, e é por isso que a frase embaixo dela continua sendo texto.
 *
 * ── O que ele não tem, de propósito ──
 * Não há "entrar com Google", "criar conta" nem "esqueci a senha": nenhum dos três
 * existe no produto. Botão que não faz nada numa tela que a Meta avalia é a mesma
 * mentira do "conexão simulada" que o ADR 0012 tirou daqui. No lugar do terceiro, a
 * frase que resolve de verdade: quem administra a operação repõe a senha.
 */
export function LoginPage(): ReactElement {
  const t = useT();
  const { me, signIn } = useAuth();
  const navigate = useNavigate();
  // Campos vazios. O login vinha preenchido com a conta de demonstração, o que
  // numa tela que o analista da Meta abre significa entregar credencial alheia
  // de graça — e sugerir que o produto tem uma conta compartilhada.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [vendoSenha, setVendoSenha] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (me !== null) return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn({ email, password });
      navigate('/', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.login.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login__form-col">
        <div className="login__box">
          <div className="login__brand">
            <ZendaWordmark className="login__logo" />
            <span className="sr-only">Zenda</span>
          </div>

          <div className="login__intro">
            <h1 className="login__title">{t.login.greeting}</h1>
            <p className="login__text">{t.login.lede}</p>
          </div>

          <form className="login__form" onSubmit={(e) => void onSubmit(e)}>
            <label className="field">
              <span className="label">{t.login.email}</span>
              <input
                className="input"
                type="email"
                autoComplete="username"
                value={email}
                maxLength={160}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <label className="field">
              <span className="label">{t.login.password}</span>
              {/* O olho fica DENTRO do campo, e é um `button`: senha digitada errada
                  em teclado de celular é o motivo nº 1 de não conseguir entrar. */}
              <span className="login__senha">
                <input
                  className="input"
                  type={vendoSenha ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="login__olho"
                  onClick={() => setVendoSenha((v) => !v)}
                  aria-label={vendoSenha ? t.login.hidePassword : t.login.showPassword}
                  aria-pressed={vendoSenha}
                >
                  <Icon name={vendoSenha ? 'eye-off' : 'eye'} size={16} />
                </button>
              </span>
            </label>

            {error === null ? null : (
              <p className="login__error">
                <Icon name="alert" size={15} />
                {error}
              </p>
            )}

            <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
              {busy ? t.login.submitting : t.login.submit}
            </button>
          </form>

          <p className="login__ajuda">{t.login.lostAccess}</p>
        </div>
      </div>

      {/* A arte entra por `background-image`, e a URL vem do bundle: `heroLogin` é o
          caminho com hash que o Vite gera, e CSS não tem como conhecê-lo. */}
      <aside
        className="login__vitrine"
        style={{ ['--arte' as string]: `url(${heroLogin})` }}
        aria-hidden="true"
      >
        <div className="login__vitrine-txt">
          <p className="login__vitrine-titulo">{t.login.panelTitle}</p>
          <p className="login__vitrine-sub">{t.login.panelSub}</p>
        </div>
      </aside>
    </div>
  );
}
